import {
  readSpecCommodities,
  readSpecsType,
  readSpecsReagents,
  updateSpecCommodities,
  updateSpecsReagents,
} from "@/services/api/specs";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { QUERY_KEYS } from "../queryKeys";
import {
  CommoditySelectionInfo,
  SpecReadReagentsResponse,
  SpecUpdateReagentsRequest,
} from "@/types/specs";
import { ProjectType } from "@/consts/Project";
import { useHandledQuery } from "./useHandledQuery";
import { ProjectInfo } from "@/types/project";

// 프로젝트 시약/장비 규격 조회(대체 품목 포함)
export const useReadSpecType = ({
  project_type,
  project_id,
}: {
  project_type: ProjectType;
  project_id: string;
}) => {
  const queryKey = QUERY_KEYS.SPEC.ROOT(project_id);
  return useHandledQuery({
    queryKey,
    queryFn: () => readSpecsType({ project_type, project_id }),
    options: {
      queryKey,
      enabled: !!project_id,
    },
  });
};

export const useReadSpecsReagents = ({
  project_type,
  project_id,
}: {
  project_type: ProjectType;
  project_id: string;
}) => {
  return useQuery({
    queryKey: QUERY_KEYS.SPEC.REAGENTS(project_id),
    queryFn: () => readSpecsReagents({ project_type, project_id }),
    enabled: !!project_id,
  });
};

export const useUpdateSpecsReagents = ({
  project_type,
  project_id,
}: {
  project_type: ProjectType;
  project_id: string;
}) => {
  const queryClient = useQueryClient();
  const queryKey = QUERY_KEYS.SPEC.REAGENTS(project_id);

  return useMutation({
    mutationFn: (data: SpecUpdateReagentsRequest) =>
      updateSpecsReagents({ project_type, project_id, data }),
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData =
        queryClient.getQueryData<SpecReadReagentsResponse>(queryKey);

      if (previousData) {
        queryClient.setQueryData<SpecReadReagentsResponse>(queryKey, (old) => {
          if (!old) return previousData;

          const updatedReagents = old.reagents.map((reagent) => {
            const updateData = newData.reagents.find(
              (r) => r.spec_id === reagent.spec_id
            );
            if (!updateData) return reagent;

            return {
              ...reagent,
              alt_items: reagent.alt_items.map((altItem) => {
                const updatedAltItem = updateData.alt_items.find(
                  (a) => a.alt_map_id === altItem.alt_map_id
                );
                if (!updatedAltItem) return altItem;

                return {
                  ...altItem,
                  selected: updatedAltItem.selected,
                };
              }),
            };
          });

          return {
            ...old,
            reagents: updatedReagents,
          };
        });
      }

      return { previousData };
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      // queryClient.invalidateQueries({ queryKey });
    },
  });
};

// 프로젝트 규격 조회 (대체 품목 포함) - 장비
export const useReadSpecCommodities = ({
  project_type,
  project_id,
}: {
  project_type: ProjectType;
  project_id: string;
}) => {
  return useQuery({
    queryKey: QUERY_KEYS.SPEC.COMMODITIES(project_id),
    queryFn: () => readSpecCommodities({ project_type, project_id }),
    enabled: !!project_id,
  });
};

// 프로젝트 규격 수정 (대체 품목 변경) - 장비
export const useUpdateSpecCommodities = ({
  project_type,
  project_id,
}: {
  project_type: ProjectType;
  project_id: string;
}) => {
  const queryClient = useQueryClient();
  const queryKey = QUERY_KEYS.SPEC.COMMODITIES(project_id);

  return useMutation({
    mutationFn: (commodity_selections: CommoditySelectionInfo[]) =>
      updateSpecCommodities({ project_type, project_id, commodity_selections }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

// project 목록으로 sepc type 순회 조회
export const useReadSpecTypeByProjects = ({
  projects,
}: {
  projects: ProjectInfo[];
}) => {
  return useQueries({
    queries: projects.map((project) => ({
      queryKey: QUERY_KEYS.SPEC.ROOT(project.project_id),
      queryFn: () =>
        readSpecsType({
          project_type: project.project_type,
          project_id: project.project_id,
        }),
      enabled: !!project.project_id,
    })),
  });
};
